import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import TenantSelector from "../components/common/TenantSelector";
import { useTenantSelection } from "../hooks/useTenantSelection";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ShippingManager from "../components/PublicSite/ShippingManager";
import PaymentManager from "../components/PublicSite/PaymentManager";
import DeliveryMethodsManager from "../components/PublicSite/DeliveryMethodsManager";
import CategoryVisibilityManager from "../components/PublicSite/CategoryVisibilityManager";
import NewsletterSection from "../components/PublicSite/Newsletter/NewsletterSection";
import ErrorBoundary from "../components/common/ErrorBoundary";

const PublicSiteManagementPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const [userProfile, setUserProfile] = useState<ProfileAndSettings | null>(
    null,
  );
  const [profileLoading, setProfileLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // ── Multi-tenant selection (ADR-021) ─────────────────────────────────────
  // Shipping/payment/delivery config is per-tenant; the keyed remount below
  // refetches when the operator switches tenant.
  const { tenants, selectedTenant, selectTenant } = useTenantSelection();

  useEffect(() => {
    profile()
      .then((p) => {
        setUserProfile(p);
        setProfileLoading(false);
      })
      .catch(() => setProfileLoading(false));
  }, [profile]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  if (profileLoading)
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        A carregar...
      </div>
    );

  if (!userProfile?.isAdmin()) return <Navigate to="/" replace />;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-1 text-gray-800">
          Gestão do Site Público
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          Configure envios, métodos de pagamento, locais de recolha, a
          visibilidade do catálogo público e as subscrições de newsletter.
        </p>

        <div className="mb-6 max-w-xs">
          <TenantSelector
            tenants={tenants}
            value={selectedTenant}
            onChange={selectTenant}
          />
        </div>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            aria-label="public-site-tabs"
          >
            <Tab
              label="Envios"
              id="tab-shipping"
              aria-controls="panel-shipping"
            />
            <Tab
              label="Pagamentos"
              id="tab-payments"
              aria-controls="panel-payments"
            />
            <Tab
              label="Entrega"
              id="tab-delivery"
              aria-controls="panel-delivery"
            />
            <Tab
              label="Catálogo"
              id="tab-catalog"
              aria-controls="panel-catalog"
            />
            <Tab
              label="Newsletter"
              id="tab-newsletter"
              aria-controls="panel-newsletter"
            />
          </Tabs>
        </Box>

        <div
          role="tabpanel"
          id="panel-shipping"
          aria-labelledby="tab-shipping"
          hidden={activeTab !== 0}
        >
          {activeTab === 0 && (
            <ErrorBoundary>
              <ShippingManager key={selectedTenant} />
            </ErrorBoundary>
          )}
        </div>
        <div
          role="tabpanel"
          id="panel-payments"
          aria-labelledby="tab-payments"
          hidden={activeTab !== 1}
        >
          {activeTab === 1 && (
            <ErrorBoundary>
              <PaymentManager key={selectedTenant} />
            </ErrorBoundary>
          )}
        </div>
        <div
          role="tabpanel"
          id="panel-delivery"
          aria-labelledby="tab-delivery"
          hidden={activeTab !== 2}
        >
          {activeTab === 2 && (
            <ErrorBoundary>
              <DeliveryMethodsManager key={selectedTenant} />
            </ErrorBoundary>
          )}
        </div>
        <div
          role="tabpanel"
          id="panel-catalog"
          aria-labelledby="tab-catalog"
          hidden={activeTab !== 3}
        >
          {activeTab === 3 && (
            <ErrorBoundary>
              <CategoryVisibilityManager key={selectedTenant} />
            </ErrorBoundary>
          )}
        </div>
        <div
          role="tabpanel"
          id="panel-newsletter"
          aria-labelledby="tab-newsletter"
          hidden={activeTab !== 4}
        >
          {activeTab === 4 && (
            <ErrorBoundary>
              <NewsletterSection key={selectedTenant} />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicSiteManagementPage;
